import { useMemo, useState } from 'react';

export const useOrdering = <T extends Record<string, any>>(
    item: T[],
    defaultSortKey: string,
    initialOrderForDefaultKey: 'asc' | 'desc' = 'asc'
) => {
    const [orderMap, setOrderMap] = useState<Record<string, 'asc' | 'desc'>>({
        [defaultSortKey]: initialOrderForDefaultKey
    });
    const [orderBy, setOrderBy] = useState<string>(defaultSortKey);

    const handleRequestSort = (property: string) => {
        const currentOrder = orderMap[property] || 'asc';
        const newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
        setOrderMap((prev) => ({ ...prev, [property]: newOrder }));
        setOrderBy(property);
    };

    const order = orderMap[orderBy] || 'asc';

    const orderedItems = useMemo(() => {
        return [...item].sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            // Handle null/undefined values first
            if (
                (aValue === null || aValue === undefined) &&
                (bValue === null || bValue === undefined)
            )
                return 0;
            if (aValue === null || aValue === undefined)
                return order === 'asc' ? 1 : -1;
            if (bValue === null || bValue === undefined)
                return order === 'asc' ? -1 : 1;

            // Handle Dayjs objects or any objects with valueOf() method (dates)
            if (
                typeof aValue === 'object' &&
                typeof aValue.valueOf === 'function' &&
                typeof bValue === 'object' &&
                typeof bValue.valueOf === 'function'
            ) {
                const comparison = aValue.valueOf() - bValue.valueOf();
                return order === 'asc' ? comparison : -comparison;
            }

            // Handle arrays - use first element
            if (Array.isArray(aValue) && Array.isArray(bValue)) {
                aValue = aValue.length > 0 ? aValue[0] : undefined;
                bValue = bValue.length > 0 ? bValue[0] : undefined;
                if (aValue === undefined || bValue === undefined) {
                    return aValue === bValue
                        ? 0
                        : aValue === undefined
                        ? 1
                        : -1;
                }
            }

            // Handle numeric values
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // Handle objects - convert to JSON string
            if (typeof aValue === 'object') {
                aValue = JSON.stringify(aValue);
            }
            if (typeof bValue === 'object') {
                bValue = JSON.stringify(bValue);
            }

            // Convert to strings for consistent comparison
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();

            if (aStr < bStr) {
                return order === 'asc' ? -1 : 1;
            }
            if (aStr > bStr) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [item, orderBy, order]);

    return {
        order,
        orderBy,
        handleRequestSort,
        orderedItems,
        orderMap
    };
};
